import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { BillsBadgeProvider } from "@/lib/bills-badge-context";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { HelpModal } from "@/components/HelpModal";
import {
  LayoutDashboard, Receipt, CircleDollarSign, CalendarRange,
  CalendarCheck2, CalendarDays, Settings as SettingsIcon, CreditCard, LogOut, Menu, X, Wallet, Landmark, Inbox, HelpCircle, BookOpen, Users, GitMerge,
} from "lucide-react";
import { useState, useEffect } from "react";
import { api } from "@/lib/api";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", Icon: LayoutDashboard, testId: "sidebar-dashboard" },
  { to: "/app/pay-periods", label: "Upcoming Pay-Periods", Icon: CalendarRange, testId: "sidebar-pay-periods" },
  { to: "/app/planning", label: "Upcoming Months", Icon: CalendarCheck2, testId: "sidebar-planning" },
  { to: "/app/year", label: "Next 12 Months", Icon: CalendarDays, testId: "sidebar-year" },
  { to: "/app/bills", label: "Manage Bills", Icon: Receipt, testId: "sidebar-bills" },
  { to: "/app/accounts", label: "Manage Bank-Cash Accts", Icon: Wallet, testId: "sidebar-accounts" },
  { to: "/app/income", label: "Manage Paydays-Income", Icon: CircleDollarSign, testId: "sidebar-income" },
  { to: "/app/debts", label: "Debts & Loans", Icon: Landmark, testId: "sidebar-debts" },
  { to: "/app/subscription", label: "Subscription", Icon: CreditCard, testId: "sidebar-subscription" },
  { to: "/app/settings", label: "Settings", Icon: SettingsIcon, testId: "sidebar-settings" },
];

const ADMIN_NAV = [
  { to: "/app/admin/users", label: "Users", Icon: Users, testId: "sidebar-admin-users" },
  { to: "/app/admin/feedback", label: "Feedback inbox", Icon: Inbox, testId: "sidebar-admin-feedback" },
  { to: "/app/admin/bill-duplicates", label: "Bill duplicates", Icon: GitMerge, testId: "sidebar-admin-bill-dupes" },
];

// Trial countdown pill — sits above Log out in the sidebar footer when
// the user's subscription is `trialing`. Dark green by default; shifts to
// warm amber in the final 5 days to signal urgency without a modal
// (Feb 2026 D3). Click routes to the existing subscription page.
function TrialPill({ trialDaysRemaining }) {
  if (trialDaysRemaining == null || trialDaysRemaining < 0) return null;
  const urgent = trialDaysRemaining <= 5;
  const cls = urgent
    ? "bg-[#FBF3E0] text-[#8A6A1A] border border-[#D8B98A] hover:bg-[#F5EAC9]"
    : "bg-[#4A6148] text-white hover:bg-[#3B5B3E]";
  const label = trialDaysRemaining === 0
    ? "Trial ends today · Upgrade"
    : `${trialDaysRemaining} day${trialDaysRemaining === 1 ? "" : "s"} left · Upgrade`;
  return (
    <Link
      to="/app/subscription"
      className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full text-xs font-semibold transition-colors ${cls}`}
      data-testid="sidebar-trial-pill"
    >
      {label}
    </Link>
  );
}

function NavList({ onClick, isAdmin, onOpenHelp }) {
  const items = isAdmin ? [...NAV, ...ADMIN_NAV] : NAV;
  return (
    <nav className="flex flex-col gap-1">
      {items.map(({ to, label, Icon, testId }) => (
        <NavLink
          key={to}
          to={to}
          data-testid={testId}
          onClick={onClick}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-colors ${
              isActive
                ? "bg-[#EEF3EC] text-[#3B5B3E]"
                : "text-[#59605C] hover:bg-[#F2EFEB] hover:text-[#2C302E]"
            }`
          }
        >
          <Icon size={18} strokeWidth={1.75} />
          <span className="flex-1">{label}</span>
        </NavLink>
      ))}
      <a
        href="/faq"
        target="_blank"
        rel="noreferrer"
        data-testid="sidebar-faq"
        onClick={onClick}
        className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-[#59605C] hover:bg-[#F2EFEB] hover:text-[#2C302E] transition-colors"
      >
        <BookOpen size={18} strokeWidth={1.75} />
        <span className="flex-1 text-left">FAQ</span>
      </a>
      <button
        type="button"
        data-testid="sidebar-help"
        onClick={() => {
          if (onClick) onClick();
          if (onOpenHelp) onOpenHelp();
        }}
        className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-[#59605C] hover:bg-[#F2EFEB] hover:text-[#2C302E] transition-colors"
      >
        <HelpCircle size={18} strokeWidth={1.75} />
        <span className="flex-1 text-left">Help</span>
      </button>
    </nav>
  );
}

export default function AppLayout() {
  return (
    <BillsBadgeProvider>
      <AppLayoutInner />
    </BillsBadgeProvider>
  );
}

function AppLayoutInner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [trialDays, setTrialDays] = useState(null);
  const isAdmin = user?.role === "admin";

  // Fetch subscription state so we can render the trial countdown pill in
  // the sidebar footer. Only shows for status=`trialing`. Silently no-ops
  // on error (subscription page still works — pill just doesn't render).
  useEffect(() => {
    let live = true;
    api.get("/subscription/me").then(({ data }) => {
      if (!live) return;
      if (data?.status === "trialing" && typeof data.trial_days_remaining === "number") {
        setTrialDays(data.trial_days_remaining);
      } else {
        setTrialDays(null);
      }
    }).catch(() => { if (live) setTrialDays(null); });
    return () => { live = false; };
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#EFE9DE] flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex w-72 shrink-0 flex-col p-6 border-r border-[#EBE6E0] bg-white/60 sticky top-0 h-screen overflow-y-auto">
        <div className="flex items-baseline gap-2.5 mb-8" data-testid="app-logo">
          <span className="w-10 h-10 self-center rounded-full bg-[#4A6148] flex items-center justify-center text-white font-wordmark tracking-tight text-xs font-bold leading-none">
            WYB
          </span>
          <div className="font-wordmark text-[#2C302E] text-[15px] leading-tight tracking-tight break-words">
            WhatsYourBalance<span className="font-wordmark-italic text-[#4A6148] text-[0.72em] ml-[1px]">.com</span>
          </div>
        </div>
        <NavList isAdmin={isAdmin} onOpenHelp={() => setHelpOpen(true)} />
        <div className="mt-auto pt-6 border-t border-[#EBE6E0] space-y-3">
          <div className="min-w-0">
            <div className="text-sm font-medium text-[#2C302E] truncate">{user?.full_name || user?.email}</div>
            <div className="text-xs text-[#8A918D] truncate">{user?.email}</div>
          </div>
          <TrialPill trialDaysRemaining={trialDays} />
          <button
            data-testid="logout-button"
            onClick={onLogout}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-medium text-[#8C3A30] bg-white border border-[#D8CFB9] hover:bg-[#F7EFEC] hover:border-[#E4C9C2] transition-colors"
            aria-label="Log out"
          >
            <LogOut size={16} strokeWidth={2} />
            <span>Log out</span>
          </button>
          <div className="flex items-center justify-center gap-2 text-[11px] text-[#8A918D] pt-1">
            <a href="/terms" target="_blank" rel="noreferrer" className="hover:text-[#4A6148]" data-testid="sidebar-link-terms">Terms</a>
            <span>·</span>
            <a href="/privacy" target="_blank" rel="noreferrer" className="hover:text-[#4A6148]" data-testid="sidebar-link-privacy">Privacy</a>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-[#EFE9DE]/95 backdrop-blur border-b border-[#D8CFB9] px-4 py-3 flex items-center justify-between">
        <div className="flex items-baseline gap-2">
          <span className="w-8 h-8 self-center rounded-full bg-[#4A6148] flex items-center justify-center text-white font-wordmark tracking-tight text-[10px] font-bold leading-none">
            WYB
          </span>
          <span className="font-wordmark text-[#2C302E] text-[13px] tracking-tight">
            WhatsYourBalance<span className="font-wordmark-italic text-[#4A6148] text-[0.72em] ml-[1px]">.com</span>
          </span>
        </div>
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(true)}
          className="btn-ghost !p-2"
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-[#2C302E]/40" onClick={() => setMobileOpen(false)}>
          <aside
            className="absolute right-0 top-0 h-full w-72 bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-8">
              <span className="font-heading font-semibold">Menu</span>
              <button
                onClick={() => setMobileOpen(false)}
                className="btn-ghost !p-2"
                aria-label="Close menu"
                data-testid="mobile-menu-close"
              >
                <X size={20} />
              </button>
            </div>
            <NavList onClick={() => setMobileOpen(false)} isAdmin={isAdmin} onOpenHelp={() => setHelpOpen(true)} />
            <div className="mt-6">
              <TrialPill trialDaysRemaining={trialDays} />
            </div>
            <button
              data-testid="mobile-logout-button"
              onClick={onLogout}
              className="mt-3 w-full btn-secondary"
            >
              <LogOut size={16} /> Log out
            </button>
          </aside>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 min-w-0 pt-16 lg:pt-0">
        <div className="max-w-6xl mx-auto p-6 md:p-8 lg:p-12 fade-in">
          <Outlet />
        </div>
      </main>

      <FeedbackWidget />
      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
